import React, { useEffect } from 'react';
import useForm, { FormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
    Container,
    Box,
    Button,
    Grid,
    Typography
} from '@material-ui/core';
import { updateProfile } from '../../../actions/userActions';
import TextField from '../../form/TextField';
import UserImageEditor from './UserImageEditor';
import AddressField from '../../form/AddressField';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        padding: theme.spacing(3),
    },
    form: {
        width: '100%',
    },
    editSection: {
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(4),
    },
    submitButton: {
        fontSize: '1.6rem',
        padding: '0.5em 2em',
        backgroundColor: '#3273dc',
        color: '#fff',
        textTransform: 'none',
        '&:hover': {
            backgroundColor: '#2366d1',
        },
    },
    submitContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: theme.spacing(6),
        marginBottom: theme.spacing(4),
    },
    gridContainer: {
        marginTop: theme.spacing(2),
    },
    gridItem: {
        width: '100%',
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            flexBasis: '100%',
        },
    },
    addressSection: {
        marginBottom: theme.spacing(4),
        [theme.breakpoints.up('md')]: {
            marginLeft: theme.spacing(2),
        },
    },
}));

const BusinessProfileEdit = (props) => {
    const classes = useStyles();
    const methods = useForm();
    const dispatch = useDispatch();
    const { setValue, register, watch } = methods;
    const currentUser = useSelector(state => state.user.currentUser);

    let selectedImage = watch('avatar');

    useEffect(() => {
        register({ name: 'avatar' });

        if (currentUser) {
            let profile = currentUser.profile;
            setValue('name', currentUser.name);
            setValue('introduction', profile.introduction);
            setValue('phone', profile.phone);
            setValue('avatar', profile.avatar);
            setValue('address', profile.address);
            if (profile.location) {
                setValue('location', {
                    lat: profile.location.coordinates[1],
                    lng: profile.location.coordinates[0],
                });
            }
            setValue('companyWebsite', profile.companyWebsite);
            setValue('facebook', profile.facebook);
            setValue('instagram', profile.instagram);
        }
    }, [currentUser, setValue, register]);

    const handleAvatarChange = image => {
        setValue('avatar', image);
    };

    const onSubmit = data => {
        let fd = { ...data };
        fd.user_type = "Business";
        if (fd.location) {
            fd.location = {
                type: "Point",
                coordinates: [fd.location.lng, fd.location.lat]
            };
        }
        dispatch(updateProfile(fd));
    };

    return (
        <Container className={classes.root}>
            <Typography variant="h3" className={classes.title}>
                企業紹介編集
            </Typography>
            <FormContext {...methods}>
                <form className={classes.form} onSubmit={methods.handleSubmit(onSubmit)}>
                    <Box className={classes.editSection}>
                        <UserImageEditor
                            onMainImageSelect={handleAvatarChange}
                            selectedImageId={selectedImage ? selectedImage.image_id : null}
                        />
                    </Box>

                    <Box className={classes.editSection}>
                        <TextField
                            label="表示名"
                            type="text"
                            placeholder="企業・雇用主名を入力してください"
                            name="name"
                            registerOptions={{ required: true, maxLength: 50, minLength: 2 }}
                        />
                    </Box>

                    <Box className={classes.editSection}>
                        <TextField
                            label="企業・雇用主紹介文(1000文字以内)"
                            type="textarea"
                            name="introduction"
                            rows={15}
                            fontSize="1.6rem"
                            info="※紹介文は求人広告に表示されます"
                            registerOptions={{ minLength: 6, maxLength: 1000 }}
                        />
                    </Box>

                    <Grid container spacing={3} className={classes.gridContainer}>
                        <Grid item xs={12} md={6} className={classes.gridItem}>
                            <Box className={classes.editSection}>
                                <TextField
                                    label="電話番号"
                                    type="tel"
                                    placeholder="電話番号を入力してください"
                                    name="phone"
                                    registerOptions={{ maxLength: 12 }}
                                />
                            </Box>
                            <Box className={classes.editSection}>
                                <TextField
                                    label="ウェブサイト"
                                    type="url"
                                    placeholder="URLを入力してください"
                                    name="companyWebsite"
                                    registerOptions={{ maxLength: 200 }}
                                />
                            </Box>
                            <Box className={classes.editSection}>
                                <TextField
                                    label="Facebook ページ"
                                    type="url"
                                    placeholder="URLを入力してください"
                                    name="facebook"
                                    registerOptions={{ maxLength: 200 }}
                                />
                            </Box>
                            <Box className={classes.editSection}>
                                <TextField
                                    label="Instagram"
                                    type="url"
                                    placeholder="URLを入力してください"
                                    name="instagram"
                                    registerOptions={{ maxLength: 200 }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6} className={classes.gridItem}>
                            <Box className={classes.addressSection}>
                                <AddressField
                                    label="所在地"
                                    placeholder="会社の住所を入力してください"
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    <Box className={classes.submitContainer}>
                        <Button
                            variant="contained"
                            type="submit"
                            className={classes.submitButton}
                            size="large"
                        >
                            変更を保存
                        </Button>
                    </Box>
                </form>
            </FormContext>
        </Container>
    );
};

export default BusinessProfileEdit;
